import React from 'react';
import { ShoppingCart, User, Receipt, Tag, Save, Printer, DollarSign, RotateCcw, TrendingUp, Wallet, X, Minus, Plus, Trash2 } from 'lucide-react';

const CartContent = React.memo(({
  cart,
  customerInfo,
  setCustomerInfo,
  orderNote,
  setOrderNote,
  couponCode,
  setCouponCode,
  appliedCoupon,
  applyCoupon,
  removeCoupon,
  discount,
  setDiscount,
  isTaxEnabled,
  setIsTaxEnabled,
  taxRate,
  setTaxRate,
  subtotal,
  couponDiscount,
  manualDiscount,
  taxAmount,
  total,
  formatKES,
  updateQuantity,
  removeFromCart,
  saveOrder,
  printReceipt, // Ensure printReceipt is destructured
  setShowPayment,
  voidTransaction,
  setShowTransactionHistory,
  setShowCartModal,
  isMobile,
  shiftStatus,
  endShift,
  startShift
}) => (
  <div className="p-4 bg-white h-full">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-bold flex items-center space-x-2 text-gray-800">
        <ShoppingCart className="h-6 w-6" />
        <span>Current Order</span>
      </h2>
      <div className="flex items-center space-x-2">
        <span className="bg-blue-600 text-white px-2 py-1 rounded-full text-sm">
          {cart.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)}
        </span>
        {isMobile && (
          <button onClick={() => setShowCartModal(false)} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        )}
      </div>
    </div>

    <div className="mb-4">
      <h3 className="font-semibold mb-2 flex items-center space-x-2 text-gray-800">
        <User className="h-4 w-4" />
        <span>Customer Info</span>
      </h3>
      <div className="space-y-2">
        <input
          type="text"
          placeholder="Customer Name"
          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          value={customerInfo.name || ''}
          onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
        />
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            placeholder="Order #"
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            value={customerInfo.table || ''}
            onChange={(e) => setCustomerInfo(prev => ({ ...prev, table: e.target.value }))}
          />
          <input
            type="tel"
            placeholder="Phone"
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            value={customerInfo.phone || ''}
            onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
          />
        </div>
      </div>
    </div>

    <div className="mb-4">
      <textarea
        placeholder="Order notes..."
        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
        rows="2"
        value={orderNote || ''}
        onChange={(e) => setOrderNote(e.target.value)}
      />
    </div>

    <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
      {cart.length === 0 ? (
        <div className="text-center py-6 text-gray-500">
          <ShoppingCart className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Your cart is empty</p>
        </div>
      ) : (
        cart.map((item) => (
          <div key={item.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-sm text-gray-800">{item.name || 'Unknown Item'}</h4>
              <button
                onClick={() => removeFromCart(item.id)}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => updateQuantity(item.id, -1)}
                  className="bg-gray-200 hover:bg-gray-300 rounded-md p-1 text-gray-700"
                >
                  <Minus className="h-3 w-3" />
                </button>
                <span className="w-8 text-center text-sm font-medium">{Number(item.quantity) || 0}</span>
                <button
                  onClick={() => updateQuantity(item.id, 1)}
                  className="bg-gray-200 hover:bg-gray-300 rounded-md p-1 text-gray-700"
                >
                  <Plus className="h-3 w-3" />
                </button>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">{formatKES(Number(item.price) || 0)}</p>
                <p className="font-bold text-blue-600">{formatKES(((Number(item.price) || 0) * (Number(item.quantity) || 0)).toFixed(2))}</p>
              </div>
            </div>
          </div>
        ))
      )}
    </div>

    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <Receipt className="h-4 w-4 text-blue-600" />
          <span className="font-medium text-gray-800">Tax Settings</span>
        </div>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={isTaxEnabled}
            onChange={() => setIsTaxEnabled(!isTaxEnabled)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span className="text-sm text-gray-600">Apply Tax</span>
        </label>
      </div>
      {isTaxEnabled && (
        <div className="flex items-center space-x-2">
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={Number(taxRate) * 100 || 0}
            onChange={(e) => setTaxRate((Number(e.target.value) || 0) / 100)}
            className="w-24 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          <span className="text-sm text-gray-600">% Tax Rate</span>
        </div>
      )}
    </div>

    <div className="mb-4">
      <div className="flex items-center space-x-2 mb-2">
        <Tag className="h-4 w-4 text-blue-600" />
        <span className="font-medium text-gray-800">Discounts</span>
      </div>
      <div className="flex space-x-2 mb-2">
        <input
          type="text"
          placeholder="Coupon code"
          className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
        />
        <button
          onClick={applyCoupon}
          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
        >
          Apply
        </button>
      </div>
      {appliedCoupon && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-700 font-medium text-sm">Coupon Applied!</p>
              <p className="text-xs text-green-600">{appliedCoupon.description}</p>
            </div>
            <button
              onClick={removeCoupon}
              className="text-green-600 hover:text-green-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center space-x-2">
        <input
          type="range"
          min="0"
          max="20"
          value={Number(discount) || 0}
          onChange={(e) => setDiscount(Number(e.target.value) || 0)}
          className="flex-1"
        />
        <span className="text-xs text-gray-500 w-16">{Number(discount) || 0}% off</span>
      </div>
    </div>

    <div className="bg-gray-50 rounded-lg p-3 mb-4 border border-gray-200">
      <h3 className="font-semibold mb-2 text-gray-800">Order Summary</h3>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between text-gray-600">
          <span>Subtotal</span>
          <span>{formatKES(subtotal.toFixed(2))}</span>
        </div>
        {couponDiscount > 0 && (
          <div className="flex justify-between text-green-600">
            <span>Coupon Discount</span>
            <span>-{formatKES(couponDiscount.toFixed(2))}</span>
          </div>
        )}
        {manualDiscount > 0 && (
          <div className="flex justify-between text-green-600">
            <span>Manual Discount ({Number(discount) || 0}%)</span>
            <span>-{formatKES(manualDiscount.toFixed(2))}</span>
          </div>
        )}
        {isTaxEnabled && taxAmount > 0 && (
          <div className="flex justify-between text-gray-600">
            <span>Tax ({(Number(taxRate) * 100).toFixed(0)}%)</span>
            <span>{formatKES(taxAmount.toFixed(2))}</span>
          </div>
        )}
        <div className="border-t border-gray-300 pt-2 mt-2">
          <div className="flex justify-between text-lg font-bold text-gray-800">
            <span>Total</span>
            <span>{formatKES(total.toFixed(2))}</span>
          </div>
        </div>
      </div>
    </div>

    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={saveOrder}
          disabled={cart.length === 0}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-2 rounded-lg font-semibold transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm"
        >
          <Save className="h-4 w-4" />
          <span>Save Order</span>
        </button>
        <button
          onClick={() => printReceipt()}
          disabled={cart.length === 0}
          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-2 rounded-lg font-semibold transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm"
        >
          <Printer className="h-4 w-4" />
          <span>Print Receipt</span>
        </button>
        <button
          onClick={() => setShowPayment(true)}
          disabled={cart.length === 0}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-400 text-white py-2 rounded-lg font-semibold transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm"
        >
          <DollarSign className="h-4 w-4" />
          <span>Process Payment</span>
        </button>
        <button
          onClick={voidTransaction}
          disabled={cart.length === 0}
          className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white py-2 rounded-lg font-semibold transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm"
        >
          <RotateCcw className="h-4 w-4" />
          <span>Clear Transaction</span>
        </button>
        <button
          onClick={() => setShowTransactionHistory(true)}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 text-sm"
        >
          <TrendingUp className="h-4 w-4" />
          <span>Recent Transaction</span>
        </button>
        {/*<button
          onClick={() => shiftStatus.active ? endShift() : startShift(prompt('Enter initial cash in drawer (Ksh):'))}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white py-2 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 text-sm"
        >
          <Wallet className="h-4 w-4" />
          <span>{shiftStatus.active ? 'End Shift' : 'Start Shift'}</span>
        </button>*/}
      </div>
    </div>
  </div>
));

export default CartContent;